import type { User, UserFormValues } from '../model/interfaces'
import type { ApiResponse } from '@shared/api'

import { getAxiosErrorMessage } from '@shared/api'
import { useAdminRoutes } from '@shared/lib/routes'
import { useToast } from '@shared/ui/toast'

import { useUsersStore } from '../model/store'

interface UserSaveOptions {
  userId: string | number
  payload: UserFormValues
}

/**
 * `PATCH /admin-api/users/{user}` — update. Only the fields present in the payload are written;
 * the backend GTO drops omitted keys (partial-update safe).
 */
export const useUserSave = async (options: UserSaveOptions): Promise<User | null> => {
  const { $axios } = useNuxtApp()
  const toast = useToast()
  const store = useUsersStore()

  const url = useAdminRoutes('admin.users.update', { user_id: options.userId })

  store.setSaving(true)

  try {
    if (!url) {
      toast.error('Unexpected error. Invalid request URL.')
      return null
    }

    const { data } = await $axios.patch<ApiResponse>(url, options.payload)

    if (!data.success) {
      toast.error(data.message)
      return null
    }

    toast.success(data.message)

    return data.data || null
  } catch (err: unknown) {
    toast.error(getAxiosErrorMessage(err))
    return null
  } finally {
    store.setSaving(false)
  }
}
